import axios from 'axios';
import {
  getSensitivePagesToken,
  isSensitiveApiUrl,
  notifySensitivePagesExpired,
} from '@/lib/sensitivePages';
import { expireManagerSession, MANAGER_TOKEN_KEY } from '@/lib/authSession';
import { withOpsBase } from '@/lib/opsBase';
import { hasTsSession, loadTsToken, clearTsSession } from '@/lib/tsIdentity';

// The API is always origin-root; only the SPA is re-based under /ops.
const API = '/api';
const TOKEN_KEY = MANAGER_TOKEN_KEY;
const LOGIN_PATH = withOpsBase('/login');

let loginRedirectStarted = false;

/**
 * A live TS session is Ops's own bearer credential — no manager-session
 * exchange. Only fall back to the legacy manager token (emergency password
 * login) when there is no TS session to use instead.
 */
export function resolveOpsBearer() {
  if (hasTsSession()) {
    return { token: loadTsToken(), kind: 'ts' };
  }
  const legacy = localStorage.getItem(TOKEN_KEY);
  return { token: legacy || null, kind: legacy ? 'legacy' : null };
}

const api = axios.create({
  baseURL: API,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const { token, kind } = resolveOpsBearer();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.__authKind = kind;
  if (isSensitiveApiUrl(config.url)) {
    const sensitiveToken = getSensitivePagesToken();
    if (sensitiveToken) {
      config.headers['X-Sensitive-Pages-Token'] = sensitiveToken;
    }
  }
  // multipart uploads must set boundary automatically; default application/json breaks /upload-image
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    if (config.headers && typeof config.headers.delete === 'function') {
      config.headers.delete('Content-Type');
    } else {
      delete config.headers['Content-Type'];
    }
  }
  return config;
});

function scheduleLoginRedirect() {
  if (window.location.pathname === LOGIN_PATH || loginRedirectStarted) return;
  loginRedirectStarted = true;
  // AuthContext handles the normal SPA redirect. Keep a hard-navigation
  // fallback for unusual cases where React does not process the event.
  window.setTimeout(() => {
    if (window.location.pathname !== LOGIN_PATH) {
      window.location.replace(LOGIN_PATH);
    }
  }, 0);
}

/**
 * A 401 always ends the credential that produced it: the TS session for a TS
 * bearer, the legacy manager token otherwise (including the emergency
 * password fallback, which never has a TS session to fall back to). A 403 is
 * left untouched in both cases — it is an authorization denial on an
 * otherwise-valid credential, not an expired one, and must not clear the
 * session or redirect.
 */
export function onApiResponseError(error) {
  const detail = error.response?.data?.detail;
  const sensitiveDetail =
    detail === 'Sensitive pages access required' ||
    detail === 'Invalid sensitive pages token' ||
    detail === 'Token expired';
  const sensitiveDenied =
    sensitiveDetail &&
    (error.response?.status === 403 || error.response?.status === 401);
  const sensitiveVerifyDenied =
    error.response?.status === 401 &&
    error.config?.url === '/sensitive-pages/verify' &&
    detail === 'Invalid password';
  if (sensitiveDenied) {
    notifySensitivePagesExpired();
  } else if (
    error.response?.status === 401 &&
    error.config?.url !== '/login' &&
    !sensitiveVerifyDenied
  ) {
    if (error.config?.__authKind === 'ts') {
      clearTsSession();
    }
    // Also drops any stale legacy token so it cannot cause a loop, and sets
    // return-to + the session-expired notice regardless of which credential expired.
    expireManagerSession();
    scheduleLoginRedirect();
  }
  return Promise.reject(error);
}

api.interceptors.response.use((response) => response, onApiResponseError);

/** Download a protected API file (e.g. daily report CSV) with auth headers. */
export async function downloadAuthenticatedFile(path, filename) {
  const { token } = resolveOpsBearer();
  const sensitiveToken = getSensitivePagesToken();
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  if (sensitiveToken) headers['X-Sensitive-Pages-Token'] = sensitiveToken;

  const res = await fetch(path, { headers });
  if (!res.ok) {
    const err = new Error('Download failed');
    err.status = res.status;
    throw err;
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
}

export const loginApi = (username, password) =>
  api.post('/login', { username, password });

// Static Data
export const getSocieties = () => api.get('/societies');
export const getOccasions = () => api.get('/occasions');
export const getWeightOptions = () => api.get('/weight-options');
export const getDeliverySlots = () => api.get('/delivery-slots');
export const getExpenseTypes = () => api.get('/expense-types');
export const getFlavors = () => api.get('/flavors');
export const getPackagingOptions = () => api.get('/packaging-options');

// Pricing
export const calculatePrice = (data) => api.post('/calculate-price', data);

// Orders
export const getOrders = (params, config = {}) => api.get('/orders', { params, ...config });
export const getOrder = (id) => api.get(`/orders/${id}`);
export const createOrder = (data) => api.post('/orders', data);
export const findExtendMatchOrder = (params) => api.get('/orders/extend-match', { params });
export const updateOrder = (id, data) => api.put(`/orders/${id}`, data);
export const deleteOrder = (id) => api.delete(`/orders/${id}`);
export const updateOrderStatus = (id, status) => api.patch(`/orders/${id}/status?status=${status}`);
export const updateOrderPayment = (id, isPaid, codCollection = null) => {
  const q = new URLSearchParams({ is_paid: String(isPaid) });
  if (codCollection) q.set('cod_collection', codCollection);
  return api.patch(`/orders/${id}/payment?${q.toString()}`);
};

/** Mark paid order as refunded — removes from dashboard collected totals immediately. */
export const refundOrderPayment = (id) => api.post(`/orders/${id}/refund`);

/** Time-limited token for the driver handoff page (Bearer: manager session). */
export const createDriverHandoff = (orderId) => api.post(`/orders/${orderId}/driver-handoff`);

export const getTeamSettings = () => api.get('/team-settings');
export const updateTeamSettings = (data) => api.put('/team-settings', data);
export const getDeliveryPartnerPayouts = () => api.get('/delivery-partner-payouts');
export const markDeliveryPartnerPaid = (partnerId, note = '') =>
  api.post(`/delivery-partner-payouts/${partnerId}/mark-paid`, { note });
export const resolveDeliveryPartnerEarningDistance = (earningId, body = {}) =>
  api.post(`/delivery-partner-payouts/earnings/${earningId}/resolve-distance`, body);

export const getDriverCollectionSettlements = (params) =>
  api.get('/driver-collection-settlements', { params });
export const getDriverCollectionSettlementSummary = (params) =>
  api.get('/driver-collection-settlements/summary', { params });
export const settleDriverCollection = (collectionId, note = '') =>
  api.post(`/driver-collection-settlements/${collectionId}/settle`, { note });
export const settleDriverCollectionsBatch = (collectionIds, note = '') =>
  api.post('/driver-collection-settlements/settle-batch', { collection_ids: collectionIds, note });
export const settleOrderDriverCollection = (orderId, note = '') =>
  api.post(`/orders/${orderId}/settle-driver-collection`, { note });

export const getPaymentAlertHealth = () => api.get('/payment-alert/health');
export const getPaymentAlertReview = (params = {}) => api.get('/payment-alert/review', { params });

// Customers
export const getCustomers = (params) => api.get('/customers', { params });
export const getCustomer = (id) => api.get(`/customers/${id}`);

// Recipes
export const getRecipes = () => api.get('/recipes');
export const getRecipe = (id) => api.get(`/recipes/${id}`);
export const updateRecipe = (id, data) => api.put(`/recipes/${id}`, data);
export const updateRecipeFrostingStructure = (id, data) =>
  api.put(`/recipes/${id}/frosting-structure`, data);

// Dry Cakes
export const getDryCakes = () => api.get('/dry-cakes');
export const getDryCake = (id) => api.get(`/dry-cakes/${id}`);
export const createDryCake = (data) => api.post('/dry-cakes', data);
export const updateDryCake = (id, data) => api.put(`/dry-cakes/${id}`, data);
export const deleteDryCake = (id) => api.delete(`/dry-cakes/${id}`);

// Cheesecakes
export const getCheesecakes = () => api.get('/cheesecakes');
export const getCheesecake = (id) => api.get(`/cheesecakes/${id}`);
export const createCheesecake = (data) => api.post('/cheesecakes', data);
export const updateCheesecake = (id, data) => api.put(`/cheesecakes/${id}`, data);
export const deleteCheesecake = (id) => api.delete(`/cheesecakes/${id}`);
export const getCheesecakesCogsReport = () => api.get('/cheesecakes-cogs-report');

// Team Recipes (kitchen reference)
export const getTeamRecipes = () => api.get('/team-recipes');
export const getTeamRecipe = (id) => api.get(`/team-recipes/${id}`);
export const createTeamRecipe = (data) => api.post('/team-recipes', data);
export const updateTeamRecipe = (id, data) => api.put(`/team-recipes/${id}`, data);
export const deleteTeamRecipe = (id) => api.delete(`/team-recipes/${id}`);

// Ingredient Prices
// Ingredient Prices
export const getIngredientPrices = () => api.get('/ingredient-prices');
export const createIngredientPrice = (data) => api.post('/ingredient-prices', data);
export const updateIngredientPrice = (id, data) => api.put(`/ingredient-prices/${id}`, data);
export const deleteIngredientPrice = (id) => api.delete(`/ingredient-prices/${id}`);

// Purchases
export const getPurchases = (params) => api.get('/purchases', { params });
export const createPurchase = (data) => api.post('/purchases', data);
export const updatePurchase = (id, data) => api.put(`/purchases/${id}`, data);
export const deletePurchase = (id) => api.delete(`/purchases/${id}`);

// Decor Ingredients Inventory
export const getDecorIngredients = (params) => api.get('/decor-ingredients', { params });
export const getDecorIngredientsStockSummary = () => api.get('/decor-ingredients/stock-summary');
export const getDecorIngredientSuggestions = (params) => api.get('/decor-ingredients/suggest', { params });
export const getDecorTaggingOrders = (params) => api.get('/decor-ingredients/tagging-orders', { params });
export const createDecorIngredient = (data) => api.post('/decor-ingredients', data);
export const updateDecorIngredient = (id, data) => api.put(`/decor-ingredients/${id}`, data);
export const deleteDecorIngredient = (id) => api.delete(`/decor-ingredients/${id}`);
export const getDecorPurchases = (params) => api.get('/decor-purchases', { params });
export const createDecorPurchase = (data) => api.post('/decor-purchases', data);
export const updateDecorPurchase = (id, data) => api.put(`/decor-purchases/${id}`, data);
export const deleteDecorPurchase = (id) => api.delete(`/decor-purchases/${id}`);
export const getOrderDecorUsage = (orderId) => api.get(`/orders/${orderId}/decor-usage`);
export const createOrderDecorUsage = (orderId, data) => api.post(`/orders/${orderId}/decor-usage`, data);
export const updateOrderDecorUsage = (orderId, usageId, data) => api.put(`/orders/${orderId}/decor-usage/${usageId}`, data);
export const deleteOrderDecorUsage = (orderId, usageId) => api.delete(`/orders/${orderId}/decor-usage/${usageId}`);

// Expenses
export const getExpenses = (params) => api.get('/expenses', { params });
export const createExpense = (data) => api.post('/expenses', data);
export const updateExpense = (id, data) => api.put(`/expenses/${id}`, data);
export const deleteExpense = (id) => api.delete(`/expenses/${id}`);

// Team tasks
export const getTasks = (params) => api.get('/tasks', { params });
export const createTask = (data) => api.post('/tasks', data);
export const updateTask = (id, data) => api.put(`/tasks/${id}`, data);
export const markTaskDone = (id) => api.patch(`/tasks/${id}/done`);
export const deleteTask = (id) => api.delete(`/tasks/${id}`);
export const sendTaskWhatsApp = (id) => api.post(`/tasks/${id}/send-whatsapp`);

// Dashboard & Analytics
export const getDashboard = () => api.get('/dashboard');
export const getAnalytics = (params) => api.get('/analytics', { params });
export const getAppAnalytics = (period) => api.get('/app-analytics', { params: { period } });
export const getCalendarOrders = (year, month) => api.get('/calendar', { params: { year, month } });
export const getCalendarSpecialEvents = (year, month) => api.get('/calendar/special-events', { params: { year, month } });
export const createCalendarSpecialEvent = (data) => api.post('/calendar/special-events', data);
export const deleteCalendarSpecialEvent = (id) => api.delete('/calendar/special-events/' + id);

// Seed Database
export const seedDatabase = () => api.post('/seed');

export default api;
